import React, { useState, useEffect, useRef } from 'react';
import './ChatWindow.css';

function ChatWindow({ customer, token, onMessageSent, onBack, refreshTick, onAuthError, appName = 'LgoKnowledge' }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [editingMeta, setEditingMeta] = useState(false);
  const [editName, setEditName] = useState('');
  const [editOrderNumber, setEditOrderNumber] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const prevCustomerPhoneRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const conversationKey = customer?.isNew ? 'new' : customer?.phone || null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const normalizePhone = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return '';
    if (hasPlus) return `+${digits}`;
    if (digits.startsWith('44')) return `+${digits}`;
    if (digits.length === 11 && digits.startsWith('07')) return `+44${digits.slice(1)}`;
    if (digits.length === 10 && digits.startsWith('7')) return `+44${digits}`;
    return '';
  };

  const isValidPhoneInput = (value) => {
    if (!value) return false;
    const trimmed = String(value).trim();
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return false;
    if (trimmed.startsWith('+')) {
      return digits.length >= 10 && digits.length <= 15;
    }
    if (digits.startsWith('44')) return digits.length >= 12;
    if (digits.length === 11 && digits.startsWith('07')) return true;
    if (digits.length === 10 && digits.startsWith('7')) return true;
    return false;
  };

  const fetchMessages = async () => {
    if (!customer || customer.isNew) return;

    try {
      const response = await fetch(`/api/messages/${encodeURIComponent(customer.phone)}`, {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await onAuthError?.();
        if (refreshed) return fetchMessages();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        const lastId = data.length ? data[data.length - 1].id : null;
        if (lastId && lastMessageIdRef.current && lastId !== lastMessageIdRef.current) {
          shouldScrollRef.current = true;
        }
        lastMessageIdRef.current = lastId;
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    if (customer && !customer.isNew) {
      prevCustomerPhoneRef.current = customer.phone || null;
      shouldScrollRef.current = true;
      fetchMessages();
      setEditingMeta(false);
      setEditName(customer.name || '');
      setEditOrderNumber(customer.order_number || '');
      // Poll for new messages every 3 seconds
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
    if (customer?.isNew) {
      setMessages([]);
      setPhone('');
      setName('');
      setOrderNumber('');
      setEditingMeta(false);
      setEditName('');
      setEditOrderNumber('');
      lastMessageIdRef.current = null;
      prevCustomerPhoneRef.current = null;
    }
    return undefined;
  }, [conversationKey]);

  useEffect(() => {
    if (customer && !customer.isNew) {
      fetchMessages();
    }
  }, [refreshTick]);

  useEffect(() => {
    if (shouldScrollRef.current) {
      shouldScrollRef.current = false;
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (editingMeta) {
      messageInputRef.current?.blur();
    }
  }, [editingMeta]);

  const handleToggleEditMeta = () => {
    if (!customer || customer.isNew) return;
    setEditingMeta((prev) => {
      const next = !prev;
      if (next) {
        setEditName(customer.name || '');
        setEditOrderNumber(customer.order_number || '');
      }
      return next;
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const normalizedNewPhone = customer?.isNew ? normalizePhone(phone) : '';
    const targetPhone = customer?.isNew ? normalizedNewPhone : customer?.phone;
    const customerName = customer?.isNew ? name : customer?.name;
    const targetOrderNumber = customer?.isNew ? orderNumber : customer?.order_number;

    if (!targetPhone) {
      alert('Please enter a valid phone number');
      return;
    }

    setSending(true);
    try {
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.cookie
            .split('; ')
            .find((row) => row.startsWith('csrf_token='))
            ?.split('=')
            .slice(1)
            .join('=') || ''
        },
        body: JSON.stringify({
          to: targetPhone,
          message: newMessage,
          customerName: customerName,
          orderNumber: targetOrderNumber
        })
      });

      if (response.status === 401 || response.status === 403) {
        const refreshed = await onAuthError?.();
        if (refreshed) return handleSend(e);
        return;
      }
      if (response.ok) {
        setNewMessage('');
        if (customer?.isNew) {
          // Switch to the conversation view
          onMessageSent(targetPhone);
        } else {
          shouldScrollRef.current = true;
          fetchMessages();
        }
      } else {
        const data = await response.json();
        alert(`Error sending message: ${data.error}`);
      }
    } catch (error) {
      alert('Error sending message. Please try again.');
      console.error('Error:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!customer?.phone || customer?.isNew) return;
    setSavingMeta(true);
    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.cookie
            .split('; ')
            .find((row) => row.startsWith('csrf_token='))
            ?.split('=')
            .slice(1)
            .join('=') || ''
        },
        body: JSON.stringify({
          phone: customer.phone,
          name: editName.trim() || null,
          orderNumber: editOrderNumber.trim() || null
        })
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await onAuthError?.();
        if (refreshed) return handleSaveMeta();
        return;
      }
      if (response.ok) {
        setEditingMeta(false);
        onMessageSent?.();
      } else {
        const data = await response.json();
        alert(`Error saving details: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving customer details:', error);
      alert('Error saving details. Please try again.');
    } finally {
      setSavingMeta(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPhone = (phoneNum) => {
    if (!phoneNum) return '';
    const cleaned = phoneNum.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phoneNum;
  };

  if (!customer) {
    return (
      <div className="chat-window empty">
        <div className="empty-state">
          <h2>Welcome to {appName} SMS</h2>
          <p>Select a conversation or start a new one</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-row">
          <button type="button" className="back-btn" onClick={onBack}>
            Back
          </button>
          {customer.isNew ? (
            <div className="new-conversation-header">
              <h2>New Conversation</h2>
            </div>
          ) : (
            <div>
              <h2>{customer.name || formatPhone(customer.phone)}</h2>
              <p className="customer-phone">{customer.name ? formatPhone(customer.phone) : ''}</p>
              {customer.order_number ? (
                <p className="customer-order">Order #{customer.order_number}</p>
              ) : null}
            </div>
          )}
        </div>
        {customer.isNew ? null : (
          <div className="chat-header-actions">
            <button
              type="button"
              className="edit-meta-btn"
              onClick={handleToggleEditMeta}
            >
              {editingMeta ? 'Cancel' : 'Edit'}
            </button>
          </div>
        )}
      </div>

      {customer.isNew ? (
        <div className="new-conversation-form">
          <div className="form-group">
            <label>Customer Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={(e) => {
                const normalized = normalizePhone(e.target.value);
                if (normalized) {
                  setPhone(normalized);
                }
              }}
              placeholder="+44 7700 900000"
              className={`phone-input ${phone && !isValidPhoneInput(phone) ? 'input-error' : ''}`}
            />
            {phone && !isValidPhoneInput(phone) ? (
              <div className="input-error-text">
                Please enter a valid phone number
              </div>
            ) : null}
          </div>
          <div className="form-group">
            <label>Customer Name (Optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="name-input"
            />
          </div>
          <div className="form-group">
            <label>Order Number (Optional)</label>
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="Order #12345"
              className="order-input"
            />
          </div>
        </div>
      ) : null}

      {!customer.isNew && editingMeta ? (
        <div className="meta-edit-form">
          <div className="form-group">
            <label>Customer Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Customer name"
            />
          </div>
          <div className="form-group">
            <label>Order Number</label>
            <input
              type="text"
              value={editOrderNumber}
              onChange={(e) => setEditOrderNumber(e.target.value)}
              placeholder="Order #12345"
            />
          </div>
          <div className="meta-edit-actions">
            <button
              type="button"
              className="save-meta-btn"
              onClick={handleSaveMeta}
              disabled={savingMeta}
            >
              {savingMeta ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="messages-container">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message ${msg.direction === 'outbound' ? 'sent' : 'received'}`}
          >
            <div className="message-bubble">
              <div className="message-text">{msg.message_body}</div>
              <div className="message-time">{formatTime(msg.timestamp)}</div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="message-input-form">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          className="message-input"
          rows="3"
          ref={messageInputRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <button
          type="submit"
          disabled={
            sending ||
            !newMessage.trim() ||
            (customer?.isNew && !isValidPhoneInput(phone))
          }
          className="send-btn"
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default ChatWindow;
